import { z } from 'zod'
import { BranchDesignator } from './BranchDesignator'
import { CodeNext } from './CodeNext'
import { RCancelReason } from './CancelReason'
import { RAnyConceptStatus } from './ConceptStatus'

// these days the server is sending UTC that validates without
// {offset: true} but in the current Training Mode exports, we are getting
// some offsets...  i don't think it's a real problem because Luxon can
// parse and use it all just fine.
const REncounterUpdateDT = z.iso.datetime({offset: true})

// looseObject = don't strip unknown keys
// useful when you have a newer renderer relying on keys that the
// older executing frontend bundle doesn't have in its export schema
export const RExportFormEncounter = z.looseObject({
	Encounter_GUID: z.uuid(),
	EncounterCreateDT: REncounterUpdateDT,
	// last time any import was pushed to update the encounter
	EncounterLastUpdateDT: REncounterUpdateDT,
	// last time the app updated the encounter's CodeNext
	EncounterAppLastStatusDT: REncounterUpdateDT,
	BranchGUID: z.uuid(),
	FormRenderNbr: z.optional(z.nullable(z.number())),
	MinimumBundle: z.optional(z.nullable(z.number())),
	// ... dan says he may send codes we don't know about ...
	// we need to show these on the dashboard and allow the form to be
	// viewable, but we lose type inference of the enum
	CodeNext: z.union([z.enum(CodeNext), z.string()]),
	// "Date of service is stamped when the encounter starts.
	//  Until that. There is no official DOS"
	DateOfService: z.optional(z.nullable(z.iso.date())),
	ClinicianNameDegree: z.optional(z.string()),
	AccessingClinicianNameDegree: z.optional(z.string()),
	// used by /api/v0/conferencing/clinician-join-url to confirm clinician
	Clinician_ID: z.optional(z.nullable(z.string())),
	Auditor_ID: z.optional(z.nullable(z.string())),
	DisplayWarnings: z.optional(z.boolean()),
	// should be the Salesforce ServiceAppointment.Id associated with the
	// encounter; however, it doesn't stay perfectly in sync with changes in
	// Salesforce because changes intra-day changes in Salesforce are not
	// automatically propogated to the database.  so it's not reliable.
	// currently not in use in the app.
	// it should be filled in for all real encounters and missing only for
	// training mode.
	Salesforce_ID: z.optional(z.string()),
	// @deprecated: 20251120
	// native db denormalized format supporting launch renderers for video visit
	Branch_Designator: z.optional(z.union([z.literal("Virtual"), z.literal("InHome"), z.null()])),
	// `Branch_Designator2` was an errant key released to production that
	// has exactly the same data as `BranchDesignatorV2`.  since it is
	// totally duplicated and causing some annoyance to figure out which one
	// to use, I've dropped it from the signature here. it is however written
	// by formExport in Go for full backward compatibiility.
	BranchDesignatorV2: z.enum(BranchDesignator),
})
export type ExportFormEncounter = z.infer<typeof RExportFormEncounter>

// looseObject = don't strip unknown keys
// useful when you have a newer renderer relying on keys that the
// older executing frontend bundle doesn't have in its export schema
export const RExportFormMember = z.looseObject({
	BirthDate: z.iso.date(),
	CMSID: z.optional(z.string()),
	// @deprecated: 20240603
	// using ClaimHistoryConcept for inbound dx history instead
	// ClaimDiagnosis: z.optional(z.array(RClaimDiagnosisCodeMap)),
	ClaimDiagnosis: z.optional(z.array(z.object({
		DiagnosisCode: z.string(),
		DiagnosisName: z.string(),
	}))),
	EmailPrimary: z.optional(z.string()),
	// "biologic gender"
	// 1 = male, 2 = female
	Gender: z.number(),
	MemberID: z.string(),
	MemberName: z.string(),
	Member_GUID: z.string(),
	PhoneMobile: z.optional(z.string()),
	PhonePrimary: z.optional(z.string()),
	ClientName: z.optional(z.string()),
	ClientLOB: z.optional(z.string()),
	IsWhiteLabelPDF: z.optional(z.boolean()),
	WhiteLabelLogoURL: z.optional(z.string()),
	IsVideoRequired: z.optional(z.boolean()),
})

// looseObject = don't strip unknown keys
// useful when you have a newer renderer relying on keys that the
// older executing frontend bundle doesn't have in its export schema
export const RExportForm = z.looseObject({
	Encounter: RExportFormEncounter,
	Member: RExportFormMember,
	CancelReason: z.array(RCancelReason),
	ConceptStatuses: z.record(z.string(), RAnyConceptStatus),
})

// looseObject = don't strip unknown keys
// useful when you have a newer renderer relying on keys that the
// older executing frontend bundle doesn't have in its export schema
export const RExport = z.looseObject({
	Form: RExportForm,
})
export type Export = z.infer<typeof RExport>
